/**
 * Order to Delivery Mapper
 * Maps order details to delivery form fields for auto-fill functionality
 * @author SAUBHAGYA Development Team
 * @since Delivery Enhancement Phase
 */

import type { OrderDetails } from '@/types/order';

export interface DeliveryFormData {
  // Pickup Details
  pickupAddress: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  pickupContactName: string;
  pickupContactPhone: string;
  requestedPickupTime?: string;

  // Drop Details
  dropAddress: string;
  dropLatitude?: number;
  dropLongitude?: number;
  dropContactName: string;
  dropContactPhone: string;

  // Product Details
  productType: string;
  quantity: number;
  productDescription?: string;
  unit: 'Kilograms' | 'Liters' | 'Units' | 'Boxes';
  weightKg: number;

  // Other Details
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  estimatedAmount?: number;
  markAsUrgent: boolean;
  additionalNotes?: string;

  // Source tracking
  assignedFrom: 'UrjaVyapar';
  batchAllocationId?: number;
  orderId?: number;
  orderNumber?: string;
}

/**
 * Sanitize phone number - remove all non-numeric characters and extract 10-digit phone
 * Handles both plain 10-digit numbers and numbers with country code (+91, etc.)
 */
function sanitizePhone(phone: string | undefined): string {
  if (!phone) return '';

  // Remove all non-numeric characters
  const digitsOnly = phone.replace(/\D/g, '');

  // If we have more than 10 digits, assume country code is present (e.g., 919876543210)
  // Extract the last 10 digits which should be the actual phone number
  if (digitsOnly.length > 10) {
    return digitsOnly.slice(-10);
  }

  // Return as-is if it's 10 digits or less
  return digitsOnly;
}

/**
 * Format address from DeliveryAddress object
 */
function formatAddress(address: any): string {
  if (!address) return '';

  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.pincode,
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Calculate total weight from line items
 */
function calculateTotalWeight(lineItems: any[]): number {
  if (!lineItems || lineItems.length === 0) return 0;

  return lineItems.reduce((total, item) => {
    // Assume quantity is weight for biogas products
    // Could be enhanced with product-specific weight conversion
    return total + (item.quantity || 0);
  }, 0);
}

/**
 * Calculate total quantity from line items
 */
function calculateTotalQuantity(lineItems: any[]): number {
  if (!lineItems || lineItems.length === 0) return 0;

  return lineItems.reduce((total, item) => {
    return total + (item.quantity || 0);
  }, 0);
}

/**
 * Get primary product type from line items
 */
function getPrimaryProductType(lineItems: any[]): string {
  if (!lineItems || lineItems.length === 0) return 'Biogas';

  // Return first product type or aggregate
  const productName = lineItems[0]?.productName || lineItems[0]?.productType || 'Biogas';
  return productName;
}

/**
 * Get product description from line items
 */
function getProductDescription(lineItems: any[]): string {
  if (!lineItems || lineItems.length === 0) return '';

  const descriptions = lineItems
    .map(item => `${item.productName || item.productType} (Qty: ${item.quantity} ${item.quantityUnit || 'Units'})`)
    .join('; ');

  return descriptions;
}

/**
 * Determine priority based on order status and amount
 */
function determinePriority(orderStatus: string, amount: number): 'Low' | 'Medium' | 'High' | 'Urgent' {
  // Urgent if READY or IN_TRANSIT status
  if (orderStatus === 'READY' || orderStatus === 'IN_TRANSIT') {
    return 'Urgent';
  }

  // High priority if confirmed with significant amount
  if (orderStatus === 'CONFIRMED' && amount > 50000) {
    return 'High';
  }

  // Medium for confirmed orders
  if (orderStatus === 'CONFIRMED') {
    return 'Medium';
  }

  // Default to low
  return 'Low';
}

/**
 * Map complete order details to delivery form data
 * @param order - Complete order details from backend
 * @returns Partial delivery form data with auto-filled values
 */
export function mapOrderToDeliveryForm(order: OrderDetails): Partial<DeliveryFormData> {
  try {
    // Validate order
    if (!order || !order.id) {
      console.warn('[OrderToDeliveryMapper] Invalid order data');
      return {
        assignedFrom: 'UrjaVyapar',
        unit: 'Kilograms',
        priority: 'Medium',
        markAsUrgent: false,
      };
    }

    // Extract product details from lineItems
    const lineItems = order.lineItems || order.items || [];
    const productType = getPrimaryProductType(lineItems);
    const quantity = calculateTotalQuantity(lineItems);
    const weightKg = calculateTotalWeight(lineItems);
    const productDescription = getProductDescription(lineItems);
    const priority = determinePriority(order.status || 'PENDING', order.totalAmount || 0);

    // Extract the first batch allocation ID (if available)
    const batchAllocationId = order.batchAllocations && order.batchAllocations.length > 0
      ? order.batchAllocations[0].allocationId
      : undefined;

    // Get delivery address - try delivery info first, then construct from customer
    let dropAddress = '';
    if (order.delivery?.address) {
      dropAddress = formatAddress(order.delivery.address);
    } else if (order.customer) {
      // Fallback to customer info if no delivery address
      dropAddress = `Customer: ${order.customer.customerName}`;
    }

    // Get contact details - try delivery info first, then customer
    const dropContactName = order.delivery?.contactPerson || order.customer?.customerName || '';
    const dropContactPhone = sanitizePhone(order.delivery?.contactPhone || order.customerPhone || order.customer?.phone || '');

    // Get manufacturing unit name from batch allocations if available
    // Priority: 1. API-provided manufacturingUnitName, 2. Extract from batch number, 3. Default
    let manufacturingUnitName = 'SAUBHAGYA Manufacturing Unit';
    if (order.batchAllocations && order.batchAllocations.length > 0) {
      const firstAllocation = order.batchAllocations[0];

      // Use API-provided manufacturing unit name if available
      if (firstAllocation.manufacturingUnitName) {
        manufacturingUnitName = firstAllocation.manufacturingUnitName;
        // Add location if available
        if (firstAllocation.manufacturingUnitLocation) {
          manufacturingUnitName = `${firstAllocation.manufacturingUnitName}, ${firstAllocation.manufacturingUnitLocation}`;
        }
      } else {
        // Fallback: Extract unit name from batch number (format: BATCH-UNIT-YYYY-XXX)
        const batchNumber = firstAllocation.batchNumber || '';
        const batchParts = batchNumber.split('-');
        if (batchParts.length >= 2) {
          // Use the product type from batch as context
          manufacturingUnitName = `SAUBHAGYA ${batchParts[1]} Production Unit`;
        }
      }
    }

    const formData: Partial<DeliveryFormData> = {
      // === PICKUP DETAILS (Manufacturing unit from order) ===
      pickupAddress: manufacturingUnitName,
      pickupContactName: 'Warehouse Manager',
      pickupContactPhone: '', // Optional - leave empty

      // === REQUIRED DELIVERY DETAILS (Auto-filled from order) ===
      dropAddress: dropAddress,
      dropContactName: dropContactName,
      dropContactPhone: dropContactPhone,

      // === PRODUCT DETAILS (Auto-filled from order items) ===
      productType: productType,
      quantity: quantity,
      productDescription: productDescription,
      unit: 'Kilograms' as const,
      weightKg: weightKg,

      // === OTHER DETAILS ===
      priority: priority,
      estimatedAmount: order.totalAmount || 0,
      additionalNotes: order.delivery?.specialInstructions || order.remarks || '',
      markAsUrgent: false,

      // === SOURCE TRACKING ===
      assignedFrom: 'UrjaVyapar',
      batchAllocationId: batchAllocationId,
      orderId: order.id,
      orderNumber: order.orderNumber,
    };

    console.log('[OrderToDeliveryMapper] Successfully mapped order to delivery form:', {
      orderId: order.id,
      orderNumber: order.orderNumber,
      productType: productType,
      quantity: quantity,
      weightKg: weightKg,
      priority: priority,
      mappedFields: Object.keys(formData).length,
    });

    return formData;
  } catch (error) {
    console.error('[OrderToDeliveryMapper] Error mapping order to delivery form:', error);
    return {
      assignedFrom: 'UrjaVyapar',
      unit: 'Kilograms',
      priority: 'Medium',
      markAsUrgent: false,
    };
  }
}

/**
 * Validate mapped data has required fields
 */
export function validateMappedData(data: Partial<DeliveryFormData>): {
  isValid: boolean;
  missingFields: string[];
} {
  // Only check REQUIRED fields for delivery
  const requiredFields = [
    'dropAddress',
    'dropContactName',
    'dropContactPhone',
  ];

  const missingFields = requiredFields.filter(
    field => !data[field as keyof DeliveryFormData]
  );

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}
